import React, { Suspense } from 'react'
// import ReactDOM from 'react-dom'
import AppFileProxy from './components/AppFileProxy/AppFileProxy.js'
import * as serviceWorker from './serviceWorker.js'
import { BrowserRouter } from 'react-router-dom'
import { setContext } from 'apollo-link-context'
import { ApolloProvider } from 'react-apollo'
import { ApolloClient, InMemoryCache } from 'apollo-boost'
import { createUploadLink } from 'apollo-upload-client'
// Subscriptions
import { split, from, ApolloLink } from 'apollo-link'
import { createClient } from 'graphql-ws'
import { print } from 'graphql'
import { getMainDefinition } from 'apollo-utilities'
import { onError } from 'apollo-link-error'
import { Observable } from 'apollo-link'

import { createRoot } from 'react-dom/client'

import { getAuthToken, clearAllTokens } from './auth/tokenManager'
import { refreshAccessToken, migrateLegacyToken } from './auth/tokenRefreshService'
import { clearUserPreferences } from './components/Utils/clearUserPreferences'
import { demoActive, maskResponseData } from './auth/demoMode'
import { GRAPHQL_ENDPOINT, GRAPHQL_WS_ENDPOINT } from './constants'

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken()
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ''
    }
  }
})

const httpLink = createUploadLink({
  uri: GRAPHQL_ENDPOINT
})

class GraphQLWsLink extends ApolloLink {
  constructor (client) {
    super()
    this.client = client
  }

  request (operation) {
    return new Observable((sink) => {
      return this.client.subscribe(
        {
          query: print(operation.query),
          variables: operation.variables,
          operationName: operation.operationName,
          extensions: operation.extensions
        },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink)
        }
      )
    })
  }
}

const wsClient = createClient({
  url: GRAPHQL_WS_ENDPOINT,
  lazy: true,
  connectionParams: () => ({
    authToken: getAuthToken()
  })
})

const wsLink = new GraphQLWsLink(wsClient)

const forceLogout = () => {
  clearAllTokens()
  localStorage.removeItem('CHAT_TOKEN')
  localStorage.removeItem('CHAT_IDENTITY')
  localStorage.removeItem('email')
  localStorage.removeItem('name')
  clearUserPreferences()
  localStorage.clear()
  window.location.href = '/login'
}

const promiseToObservable = (promise) =>
  new Observable((subscriber) => {
    promise.then(
      (value) => {
        if (subscriber.closed) return
        subscriber.next(value)
        subscriber.complete()
      },
      (err) => {
        if (subscriber.closed) return
        subscriber.error(err)
      }
    )
  })

const PUBLIC_OPERATIONS = ['login', 'forgotPassword', 'register', 'changePassword']

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isPublicOperation = PUBLIC_OPERATIONS.includes(operation.operationName)

  if (isPublicOperation) return

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (!err) continue
      const dataCode = err.extensions?.data?.code || err.data?.code || ''
      const errorCode = err.extensions?.code || ''
      const errorMessage = err.message || ''
      const errorExceptionName = err.extensions?.exception?.name || ''

      console.log('[Auth ErrorLink] GraphQL error:', { dataCode, code: errorCode, message: errorMessage, exceptionName: errorExceptionName })

      if (dataCode === 'LEGACY_TOKEN' || errorCode === 'LEGACY_TOKEN' || errorMessage.includes('LegacyTokenError') || errorMessage.includes('Token legacy')) {
        return promiseToObservable(
          migrateLegacyToken().then((success) => {
            if (!success) {
              forceLogout()
              throw new Error('Migration failed')
            }
          })
        ).flatMap(() => {
          const oldHeaders = operation.getContext().headers
          operation.setContext({
            headers: {
              ...oldHeaders,
              authorization: `Bearer ${getAuthToken()}`
            }
          })
          return forward(operation)
        })
      }

      if (dataCode === 'TOKEN_EXPIRED' || errorCode === 'TOKEN_EXPIRED' || errorMessage.includes('TokenExpiredError') || errorMessage.includes('jwt expired')) {
        return promiseToObservable(
          refreshAccessToken().then((success) => {
            if (!success) {
              forceLogout()
              throw new Error('Refresh failed')
            }
          })
        ).flatMap(() => {
          const oldHeaders = operation.getContext().headers
          operation.setContext({
            headers: {
              ...oldHeaders,
              authorization: `Bearer ${getAuthToken()}`
            }
          })
          return forward(operation)
        })
      }

      if (dataCode === 'AUTH_ERROR' || errorCode === 'AUTH_ERROR') {
        const hasRefreshToken = !!localStorage.getItem('refresh-token')
        console.log('[Auth ErrorLink] AUTH_ERROR — refresh-token present:', hasRefreshToken)
        if (!hasRefreshToken) {
          console.log('[Auth ErrorLink] No refresh token available, forcing logout')
          forceLogout()
          return
        }
        return promiseToObservable(
          refreshAccessToken().then((success) => {
            console.log('[Auth ErrorLink] AUTH_ERROR refresh result:', success)
            if (!success) {
              forceLogout()
              throw new Error('Refresh failed')
            }
          })
        ).flatMap(() => {
          const oldHeaders = operation.getContext().headers
          operation.setContext({
            headers: {
              ...oldHeaders,
              authorization: `Bearer ${getAuthToken()}`
            }
          })
          return forward(operation)
        })
      }
    }
  }

  if (networkError && networkError.statusCode === 401) {
    return promiseToObservable(
      refreshAccessToken().then((success) => {
        if (!success) {
          forceLogout()
          throw new Error('Refresh failed')
        }
      })
    ).flatMap(() => {
      const oldHeaders = operation.getContext().headers
      operation.setContext({
        headers: {
          ...oldHeaders,
          authorization: `Bearer ${getAuthToken()}`
        }
      })
      return forward(operation)
    })
  }
})

// Modo demo: enmascara clientes/montos en la respuesta (solo internos + flag).
// Se excluyen queries de UI/config (menú, rutas, permisos) para no romper la navegación.
const DEMO_SKIP_OPS = new Set(['getTranslate', 'getMenuPermissions'])
const demoMaskLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    if (response && response.data && demoActive() && !DEMO_SKIP_OPS.has(operation.operationName)) {
      response.data = maskResponseData(response.data)
    }
    return response
  })
)

const link = from([
  errorLink,
  demoMaskLink,
  split(
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query)
      return kind === 'OperationDefinition' && operation === 'subscription'
    },
    wsLink,
    authLink.concat(httpLink)
  )
])

const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    addTypename: false
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only'
    },
    query: {
      fetchPolicy: 'network-only'
    }
  }
})

const container = document.getElementById('root')
const root = createRoot(container) // createRoot(container!) if you use TypeScript

root.render(
  <BrowserRouter>
    <ApolloProvider client={client}>
      <AppFileProxy />
    </ApolloProvider>
  </BrowserRouter>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister()
